// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import usePromotionCodeForm from "@insite/client-framework/Common/Hooks/usePromotionCodeForm";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canApplyPromotionsToCart,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import applyPromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyPromotion";
import removePromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotion";
import removePromotions from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotions";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const promotions = promotionsDataView.value;
    return {
        canApplyPromosToCart: canApplyPromotionsToCart(cartState?.value),
        promotionErrorMessage: state.pages.checkoutReviewAndSubmit.promotionErrorMessage,
        promotions,
    };
};

const mapDispatchToProps = {
    applyPromotion,
    removePromotion,
    removePromotions,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory &
    HasToasterContext;

interface OnePageCheckoutPromotionStyles {
    promoContainer?: InjectableCss;
    promoFieldContainer?: InjectableCss;
    promotionErrorMessage?: TypographyPresentationProps;
    promotionTagsContainer?: InjectableCss;
    promotionTagsItem?: InjectableCss;
    promotionCodeTag?: TagPresentationProps;
    enterPromotionCodeTextField?: TextFieldPresentationProps;
    enterPromotionCodeApplyButton?: ButtonPresentationProps;
    removeCodesLink?: LinkPresentationProps;
    removeCodesLinkWrapper?: InjectableCss;
}

export const onePageCheckoutPromotionStyles: OnePageCheckoutPromotionStyles = {};

const styles = onePageCheckoutPromotionStyles;
const OnePageCheckoutPromotion: FC<Props> = ({
    promotions,
    canApplyPromosToCart,
    promotionErrorMessage,
    applyPromotion,
    removePromotion,
    removePromotions,
}) => {
    const {
        promotionCode,
        setPromotionCode,
        promotionsWithCode,
        setRemovingPromotionCode,
        isAddingDiscount,
        setIsAddingDiscount,
        isUpdatingPromotions,
        setIsUpdatingPromotions,
    } = usePromotionCodeForm(promotions);

    const applyPromotionHandler = () => {
        setIsUpdatingPromotions(true);
        applyPromotion({
            promotionCode,
            onComplete: (
                result: Partial<{
                    successMessage: string;
                    errorMessage: string;
                    isPromotionAppliedSuccess: boolean;
                }>,
            ) => {
                if (result.isPromotionAppliedSuccess) {
                    setPromotionCode("");
                    setIsAddingDiscount(false);
                }
                setIsUpdatingPromotions(false);
            },
        });
    };

    const removePromotionCodesHandler = () => {
        removePromotions();
    };

    const removePromotionHandler = (promotion: PromotionModel) => {
        setIsUpdatingPromotions(true);
        removePromotion({
            promotionId: promotion.id,
            onComplete: () => {
                setIsUpdatingPromotions(false);
            },
        });
        setRemovingPromotionCode(promotion.promotionCode);
    };

    if (!canApplyPromosToCart) {
        return null;
    }

    return (
        <LoadingOverlay loading={isUpdatingPromotions}>
            <StyledWrapper {...styles.promoContainer}>
                <StyledWrapper {...styles.promoFieldContainer} data-test-selector="promo-fields-payment-details">
                    <TextField
                        sizeVariant="small"
                        onChange={event => {
                            setPromotionCode(event.target.value);
                        }}
                        onKeyDown={event => {
                            if (event.keyCode === 13) {
                                applyPromotionHandler();
                                event.preventDefault();
                            }
                        }}
                        value={promotionCode}
                        placeholder={translate("Enter Promo Code")}
                        {...styles.enterPromotionCodeTextField}
                    />
                    <Button
                        onClick={applyPromotionHandler}
                        disabled={!(promotionCode?.length > 0)}
                        aria-label={translate("Apply")}
                        {...styles.enterPromotionCodeApplyButton}
                    >
                        {translate("Apply")}
                    </Button>
                </StyledWrapper>
                <Typography {...styles.promotionErrorMessage}>{promotionErrorMessage}</Typography>
                {promotionsWithCode.length > 1 && (
                    <StyledWrapper {...styles.removeCodesLinkWrapper}>
                        <Link
                            {...styles.removeCodesLink}
                            onClick={e => {
                                e.preventDefault();
                                removePromotionCodesHandler();
                            }}
                            data-test-selector="promotionCode_removePromotionCodesButton"
                        >
                            {translate("Remove Codes")}
                        </Link>
                    </StyledWrapper>
                )}
                {promotionsWithCode.length > 0 && (
                    <StyledWrapper {...styles.promotionTagsContainer}>
                        {promotionsWithCode.map(promotion => (
                            <StyledWrapper key={promotion.id} {...styles.promotionTagsItem}>
                                <Tag
                                    onDelete={e => {
                                        e.preventDefault();
                                        removePromotionHandler(promotion);
                                    }}
                                    data-test-selector={`promotionCode_promotionTag_${promotion.promotionCode}`}
                                    {...styles.promotionCodeTag}
                                >
                                    {promotion.name}: {promotion.promotionCode}
                                </Tag>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutPromotion),
    definition: {
        displayName: "Promotion",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
